import { CircleAlert } from 'lucide-react';
import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, Ref, SelectHTMLAttributes } from 'react';
import { cx } from '../utils/cx';

interface FieldChromeProps {
  /** Libellé visible obligatoire (WCAG 3.3.2). */
  label: string;
  hint?: ReactNode;
  /** Message d'erreur : rend le champ invalide et l'associe au contrôle. */
  error?: string | undefined;
  /** Masque visuellement le libellé tout en le gardant pour les lecteurs d'écran. */
  hideLabel?: boolean;
}

function useFieldIds(id: string | undefined) {
  const generated = useId();
  const controlId = id ?? generated;
  return { controlId, hintId: `${controlId}-hint`, errorId: `${controlId}-error` };
}

function describedBy(...ids: (string | false | undefined)[]): string | undefined {
  const value = ids.filter(Boolean).join(' ');
  return value === '' ? undefined : value;
}

function FieldMessages({
  hint,
  error,
  hintId,
  errorId,
}: {
  hint?: ReactNode;
  error?: string | undefined;
  hintId: string;
  errorId: string;
}) {
  return (
    <>
      {hint ? (
        <p id={hintId} className="av-field__hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="av-field__error">
          <CircleAlert className="av-icon" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : null}
    </>
  );
}

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldChromeProps {
  variant?: 'default' | 'code';
  ref?: Ref<HTMLInputElement>;
}

export function Input({
  label,
  hint,
  error,
  hideLabel = false,
  variant = 'default',
  id,
  className,
  ...rest
}: InputProps) {
  const { controlId, hintId, errorId } = useFieldIds(id);
  return (
    <div className={cx('av-field', className)}>
      <label htmlFor={controlId} className={hideLabel ? 'av-visually-hidden' : 'av-field__label'}>
        {label}
      </label>
      <input
        id={controlId}
        className={cx('av-input', variant === 'code' && 'av-input--code')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(hint !== undefined && hintId, error && errorId)}
        {...rest}
      />
      <FieldMessages hint={hint} error={error} hintId={hintId} errorId={errorId} />
    </div>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldChromeProps {
  ref?: Ref<HTMLSelectElement>;
}

export function Select({
  label,
  hint,
  error,
  hideLabel = false,
  id,
  className,
  children,
  ...rest
}: SelectProps) {
  const { controlId, hintId, errorId } = useFieldIds(id);
  return (
    <div className={cx('av-field', className)}>
      <label htmlFor={controlId} className={hideLabel ? 'av-visually-hidden' : 'av-field__label'}>
        {label}
      </label>
      <select
        id={controlId}
        className="av-select"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(hint !== undefined && hintId, error && errorId)}
        {...rest}
      >
        {children}
      </select>
      <FieldMessages hint={hint} error={error} hintId={hintId} errorId={errorId} />
    </div>
  );
}
